import { html, unsafeCSS } from 'lit';
import { state, customElement, property } from 'lit/decorators.js';
import ResizeObserver from 'resize-observer-polyfill';
import { KeyboardKeyEvents } from '@trp-dms-digital-shared/ds-ui-library-common';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { faiLogin, faiLoginStatus } from '../../common/utils/faiUserService.js';
import { FAILoginRegistrationCustomEvents } from '../../common/constants/index.js';
import { Constants } from './literature.constants.js';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import type { Literature, Literatures } from './literature.model.js';
import style from './literature.scss?inline';
import type {
  BeaconPopover,
  BeaconButton,
} from '@trp-beacon-ui-kit/foundation/components/index.js';

/**
 * @tag pop-literature-component
 * @property {LiteratureComponentObject} [componentObject] Literatures set
 * @attribute {LiteratureComponentObject} [data-component-object] Literatures set
 * @see https://rally1.rallydev.com/#/?detail=/userstory/798312527387&fdp=true
 */
@customElement(Constants.TAG_NAME)
export class PopLiteratureComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @state()
  isUserLoggedIn = false;

  /**
   * @internal
   */
  renderedLiteratures = false;

  /**
   * @internal
   */
  maxVisibleButtons = 3;

  /**
   * @internal
   */
  displayedLiteratures: Literature[] = [];

  /**
   * @internal
   */
  moreLiteratures: Literature[] = [];

  /**
   * @internal
   */
  private resizeObserver!: ResizeObserver;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: Literatures;

  constructor() {
    super();
  }

  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);

    if (
      !this.renderedLiteratures &&
      Array.isArray(this.componentObject?.literatures) &&
      this.componentObject?.literatures.length > 0
    ) {
      this.updateLiteratures(this.maxVisibleButtons);
    }
  }

  updateLoginState() {
    this.isUserLoggedIn = faiLoginStatus();
  }

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(
      this.updateMaxVisibleButtons.bind(this),
    );
    this.resizeObserver?.observe(document.body);

    document.addEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_IN,
      this.updateLoginState.bind(this),
    );

    document.addEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_OUT,
      this.updateLoginState.bind(this),
    );
    this.updateMaxVisibleButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();

    document.removeEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_IN,
      this.updateLoginState.bind(this),
    );
    document.removeEventListener(
      FAILoginRegistrationCustomEvents.LOGGED_OUT,
      this.updateLoginState.bind(this),
    );
  }

  updateMaxVisibleButtons() {
    const screenWidth = window.innerWidth;
    let newMaxVisibleButtons;

    if (screenWidth < 640) {
      newMaxVisibleButtons = 0;
    } else if (screenWidth < 960) {
      newMaxVisibleButtons = 1;
    } else {
      newMaxVisibleButtons = 3;
    }
    this.updateLiteratures(newMaxVisibleButtons);

    this.maxVisibleButtons = newMaxVisibleButtons;
  }

  getChildrenToMove(container: HTMLElement, start: number, end: number) {
    return Array.from(container.children).slice(start, end);
  }

  sortChildrenByRank(children: Element[], ascending = true) {
    return children.sort((a, b) => {
      const rankA = parseInt(a.getAttribute(Constants.DATA_RANK) || '');
      const rankB = parseInt(b.getAttribute(Constants.DATA_RANK) || '');

      return ascending ? rankA - rankB : rankB - rankA;
    });
  }

  moveChildren(
    targetContainer: HTMLElement,
    childrenToMove: Element[],
    prepend = false,
  ) {
    for (const child of childrenToMove) {
      if (prepend) {
        targetContainer.prepend(child);
      } else {
        targetContainer.appendChild(child);
      }
    }
  }

  updateLiteratures(newMaxVisibleButtons: number) {
    if (this.renderedLiteratures) {
      // Get the containers using querySelector
      const visibleContainer = this.shadowRoot?.querySelector<HTMLElement>(
        Constants.DISPLAYED_LITERATURES_ID,
      );
      const moreContainer = this.shadowRoot?.querySelector<HTMLElement>(
        Constants.MORE_LITERATURES_ID,
      );
      if (!visibleContainer || !moreContainer) {
        return;
      }
      if (newMaxVisibleButtons > this.maxVisibleButtons) {
        const childrenToMove = this.sortChildrenByRank(
          this.getChildrenToMove(
            moreContainer,
            0,
            newMaxVisibleButtons - this.maxVisibleButtons,
          ),
        );

        this.moveChildren(visibleContainer, childrenToMove);
      } else if (newMaxVisibleButtons < this.maxVisibleButtons) {
        const childrenToMove = this.sortChildrenByRank(
          this.getChildrenToMove(
            visibleContainer,
            newMaxVisibleButtons,
            this.maxVisibleButtons,
          ),
          false,
        );

        this.moveChildren(moreContainer, childrenToMove, true);
      }
    } else {
      //For Initial Literature Button Rendering
      this.displayedLiteratures = [
        ...(this.componentObject?.literatures.slice(0, newMaxVisibleButtons) ||
          []),
      ];

      this.moreLiteratures = [
        ...(this.componentObject?.literatures.slice(newMaxVisibleButtons) ||
          []),
      ];

      this.renderedLiteratures = true;
    }
  }

  renderLiteratureButton(literature: Literature) {
    return html`
      <li class="literature-button-row" data-rank="${literature.rank}">
        <beacon-button
          @beacon-event-click=${(e) => {
            this.handleLiteratureClick(e, literature);
          }}
          icon-align="right"
          variant="tertiary"
          scale="jumbo"
          class="literature-button"
        >
          ${literature.name} ${this.renderLiteratureIcon(literature)}
        </beacon-button>
      </li>
    `;
  }

  renderLiteratureIcon(literature: Literature) {
    return literature.locked && !this.isUserLoggedIn
      ? this.renderLockedLiteratureIcon()
      : this.renderDefaultLiteratureIcon();
  }

  renderDefaultLiteratureIcon() {
    return html`<beacon-icon slot="suffix" name="download"></beacon-icon>`;
  }

  renderLockedLiteratureIcon() {
    return html`<beacon-icon slot="suffix" name="locked"></beacon-icon>`;
  }

  //With the refresh of the beacon-popover, a revisit of the component is needed to ensure the correct behavior
  //Structure of the display/slots has change and this affects the behavior of the component
  //Migrating to <beacon-popover> and <beacon-menu> should be reviewed with design:
  //https://ui-kit.gitlab-pages.awstrp.net/foundation/main/storybook/?path=/docs/components-popover-menu--docs
  //
  //The current implementation is a draft as the literature-popover code was removed.
  renderMoreSection() {
    return html`
      <beacon-button
        @beacon-event-click=${this.handleMoreLiteratureClick}
        id="more-popover-button"
        variant="tertiary"
        scale="jumbo"
        class="more-popover-button"
      >
        More
        <beacon-icon slot="suffix" name="overflow_horizontal"></beacon-icon>
      </beacon-button>
      <beacon-popover
        id="more-popover"
        opens-by="#more-popover-button"
        @keydown=${this.handleKeyDown}
      >
        <beacon-menu>
          <ul id="more-literatures">
            ${this.moreLiteratures?.map((literature) =>
              this.renderLiteratureButton(literature),
            )}
          </ul>
        </beacon-menu>
      </beacon-popover>
    `;
  }

  handleKeyDown(event: KeyboardEvent) {
    const morePopover: BeaconPopover = this.shadowRoot?.querySelector(
      Constants.MORE_POPOVER_ID,
    );
    const moreLiteratureListContainer =
      this.shadowRoot?.querySelector<HTMLElement>(
        Constants.MORE_LITERATURES_ID,
      );
    const popoverButton: BeaconButton = this.shadowRoot?.querySelector(
      Constants.MORE_POPOVER_BUTTON_ID,
    );
    const literatureElementsInPopover =
      moreLiteratureListContainer?.querySelectorAll(Constants.BEACON_BUTTON) ||
      [];
    const [firstLiteratureElementInPopover, lastLiteratureElementInPopover] = [
      literatureElementsInPopover[0],
      literatureElementsInPopover[literatureElementsInPopover.length - 1],
    ];
    const activeElement = this.shadowRoot?.activeElement;

    const closePopover = () => {
      this.updateMoreLiteratureButtonDisclosureState(false);
      morePopover?.close();
    };

    const isTabEvent = event.key === KeyboardKeyEvents.TAB;
    const isShiftEvent = event.shiftKey;
    const isEscapeEvent =
      event.key === KeyboardKeyEvents.ESCAPE && morePopover?.isOpen;
    const isLastElementActive =
      activeElement === lastLiteratureElementInPopover;
    const isFirstElementActive =
      activeElement === firstLiteratureElementInPopover;
    const isPopoverButtonActive = activeElement === popoverButton;

    if (isEscapeEvent) {
      this.updateMoreLiteratureButtonDisclosureState(false);
      popoverButton?.focus();
    } else if (isTabEvent && !isShiftEvent && isLastElementActive) {
      closePopover();
    } else if (isTabEvent && isShiftEvent) {
      if (isFirstElementActive) {
        closePopover();
      } else if (isPopoverButtonActive) {
        if (morePopover) {
          closePopover();
        } else {
          console.error(
            'Could not close the "More" popover. The element was not found.',
          );
        }
      }
    }
  }

  handleLiteratureClick(event: Event, literature: Literature) {
    if (literature.locked && !this.isUserLoggedIn) {
      event.preventDefault();
      faiLogin();
    } else {
      window.open(literature.url, Constants.BLANK);
    }
  }

  /**
   * @internal
   */
  getMoreBeaconNativeButton(): HTMLElement | null | undefined {
    const moreBeaconButton = this.shadowRoot?.querySelector(
      Constants.MORE_POPOVER_BUTTON_ID,
    );
    return moreBeaconButton?.shadowRoot?.querySelector(Constants.BUTTON);
  }

  /**
   * @internal
   */
  getMoreButtonAriaExpandedState = () => {
    const nativeButton = this.getMoreBeaconNativeButton();
    const ariaExpanded = nativeButton?.getAttribute(Constants.ARIA_EXPANDED);

    return ariaExpanded ? ariaExpanded.toLowerCase() === Constants.TRUE : false;
  };

  /**
   * @internal
   */
  handleMoreLiteratureClick = () => {
    this.updateMoreLiteratureButtonDisclosureState(
      !this.getMoreButtonAriaExpandedState(),
    );
  };

  /**
   * @internal
   */
  updateMoreLiteratureButtonDisclosureState = (expanded: boolean) => {
    const nativeButton = this.getMoreBeaconNativeButton();
    nativeButton?.setAttribute(Constants.ARIA_EXPANDED, expanded.toString());
  };

  render() {
    return this.renderComponent(html`
      <div class="literature-button-container">
        <ul id="displayed-literatures">
          ${this.displayedLiteratures?.map((literature) =>
            this.renderLiteratureButton(literature),
          )}
          <li>
            ${this.componentObject?.displayMore &&
            this.componentObject?.literatures.length > this.maxVisibleButtons
              ? this.renderMoreSection()
              : html``}
          </li>
        </ul>
      </div>
    `);
  }
}
